import { Divider, Typography, useTheme, List, ListItem, ListItemText, Box, Collapse, IconButton } from '@mui/material'
import { black, grey } from '../../../colors'
import React, { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { UserManager } from '../../../APIManager'
import GenericModal from '../../../components/GenericModal'
import { notification } from '../../../components/Notifications'
import { ButtonType } from '../../../components/GenericButton/styles'
import type { IEnrollment } from '../../../models/Enrollment'
import type { FC } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { interpretRoleAssignment } from '../../../helpers/roleHelpers'

const STUDENT_ROLE_NAME = 'Student'

interface UpdateEnrollmentsProps {
  newlyEnrolled: any[]
  newlyUnenrolled: any[]
  isConfirmModalOpen: boolean
  setIsConfirmModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  handleSubmit: () => void | Promise<void>
  sectionId: string
  termId: string
  studentRoleId: string
  onAllClose?: () => void
}

export const UpdateEnrollments: React.FC<UpdateEnrollmentsProps> = ({
  newlyEnrolled,
  newlyUnenrolled,
  isConfirmModalOpen,
  setIsConfirmModalOpen,
  handleSubmit,
  sectionId,
  termId,
  studentRoleId,
  onAllClose
}) => {
  const theme = useTheme()
  const [isLoading, setIsLoading] = useState(false)
  const [enrolledOpen, setEnrolledOpen] = useState(true)
  const [unenrolledOpen, setUnenrolledOpen] = useState(true)
  const multipleChanges = newlyEnrolled.length + newlyUnenrolled.length > 1

  const addMutation = useMutation({
    mutationFn: async (enrollment: IEnrollment) => {
      const userId = enrollment?.userId || ''
      const response = await UserManager.postUserRoleApi({
        userId,
        email: enrollment?.email,
        objectId: sectionId,
        sectionId,
        roleId: STUDENT_ROLE_NAME,
        ...(termId ? { termId } : {}),
        instructors: []
      })
      const result = response.data || []
      const { alreadyExists } = interpretRoleAssignment(result)
      if (alreadyExists) {
        const assignedSectionId = getAssignedSectionId(result)
        if (assignedSectionId !== sectionId) {
          throw new Error('The student role already exists, but not for this course section. The enrollment was not saved.')
        }
      }
      return result
    },
    onError: (error: unknown) => {
      console.error('Error adding student role:', error)
    }
  })

  const deleteMutation = useMutation({
    mutationFn: async (enrollment: IEnrollment) => {
      const userId = enrollment?.userId || ''
      return (await UserManager.deleteStudentRoleByIdAndRoleIdApi(userId, studentRoleId, { sectionId })).data || []
    },
    onError: (error: unknown) => {
      console.error('Error deleting student role:', error)
    }
  })

  const handleCloseModal = (): void => { setIsConfirmModalOpen(false) }

  const handleUpdate = async (): Promise<void> => {
    setIsLoading(true)
    try {
      // Use allSettled so a single rejected add (e.g. the wrong-section guard)
      // does not abort the rest: the remaining adds and every un-enrollment
      // still run, and we can report exactly what persisted instead of a
      // misleading all-or-nothing error.
      const addResults = await Promise.allSettled(
        newlyEnrolled.map(async enrollment => await addMutation.mutateAsync(enrollment))
      )
      const deleteResults = await Promise.allSettled(
        newlyUnenrolled.map(async enrollment => await deleteMutation.mutateAsync(enrollment))
      )

      const failures = [...addResults, ...deleteResults].filter(
        (result): result is PromiseRejectedResult => result.status === 'rejected'
      )
      const successCount = addResults.length + deleteResults.length - failures.length

      // Everything went through.
      if (failures.length === 0) {
        await handleSubmit()
        notification('Enrollments updated successfully.', { variant: 'success', persist: true })
        setIsConfirmModalOpen(false)
        if (onAllClose) onAllClose()
        return
      }

      // Nothing persisted — keep the modal open and surface the specific reason
      // so the user can fix it and retry the whole set.
      if (successCount === 0) {
        const reason = failures[0]?.reason
        const message = reason instanceof Error
          ? reason.message
          : 'An error occurred while updating enrollments. Please Try Again'
        notification(message, { variant: 'error' })
        return
      }

      // Partial success: some changes already saved server-side. Refresh so the
      // table reflects reality, then report what failed so the user does not
      // re-submit the changes that already went through.
      await handleSubmit()
      notification(
        `${successCount} enrollment change${successCount === 1 ? '' : 's'} saved, but ${failures.length} could not be completed. The table has been refreshed — please retry the remaining change${failures.length === 1 ? '' : 's'}.`,
        { variant: 'error', persist: true }
      )
      setIsConfirmModalOpen(false)
      if (onAllClose) onAllClose()
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : 'An error occurred while updating enrollments. Please Try Again'
      notification(message, { variant: 'error' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <GenericModal
      isOpen={isConfirmModalOpen}
      onClose={handleCloseModal}
      onConfirm={handleUpdate}
      title="Updating Enrollments"
      modalWidth={theme.spacing(136)}
      confirmButtonLoading={isLoading}
      destructiveButtonType={ButtonType.Secondary}
    >

      <Typography mt={4} mb={3} fontSize={theme.spacing(3.5)} lineHeight={theme.spacing(5)} color={grey[850]}>
        You are about to change the enrollment status of {multipleChanges ? 'multiple students' : 'a student'}. Do you wish to continue?
      </Typography>
      <Box display="flex" alignItems="center" mt={3}>
        <Typography variant='h5' fontSize={theme.spacing(4)} color={black[100]} sx={{ flexGrow: 1 }}>
          Newly Enrolled in Course Section ({newlyEnrolled.length})
        </Typography>
        <IconButton size="small" onClick={() => { setEnrolledOpen(o => !o) }}>
          {enrolledOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={enrolledOpen} timeout="auto" unmountOnExit>
        <Box mb={3}>
          <EnrollmentList students={newlyEnrolled} emptyText="No new enrollments." />
        </Box>
      </Collapse>
      <Divider sx={{ my: 2 }} />
      <Box display="flex" alignItems="center" mt={3}>
        <Typography variant='h5' fontSize={theme.spacing(4)} color={black[100]} sx={{ flexGrow: 1 }}>
          Unenrolled from Course Section ({newlyUnenrolled.length})
        </Typography>
        <IconButton size="small" onClick={() => { setUnenrolledOpen(o => !o) }}>
          {unenrolledOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={unenrolledOpen} timeout="auto" unmountOnExit>
        <Box mb={3}>
          <EnrollmentList students={newlyUnenrolled} emptyText="No unenrollments." />
        </Box>
      </Collapse>

    </GenericModal>
  )
}

const getAssignedSectionId = (result: unknown): string | undefined => {
  if (result == null || typeof result !== 'object') return undefined
  const body = result as { section?: { sectionId?: string | null } | null, sectionId?: string | null }
  return body.section?.sectionId ?? body.sectionId ?? undefined
}

const EnrollmentList: FC<{ students: any[], emptyText: string }> = ({ students, emptyText }) => {
  const theme = useTheme()
  if (!students || students.length === 0) {
    return <Typography color={grey[600]} fontSize={theme.spacing(3)}>{emptyText}</Typography>
  }
  return (
    <List dense>
      {students?.map((e, idx) => (
        <ListItem key={e.userId || idx} sx={{ mb: 1 }}>
          <ListItemText
            primary={<span style={{ fontWeight: 500 }}>{e.firstName} {e.lastName}</span>}
            secondary={e.email}
            primaryTypographyProps={{ style: { fontSize: theme.spacing(3.2), color: black[100] } }}
            secondaryTypographyProps={{ style: { fontSize: theme.spacing(3), color: grey[600] } }}
          />
        </ListItem>
      ))}
    </List>
  )
}
